// Kontaktformular und „Verträge hier kündigen" (§ 312k BGB) — ohne Anmeldung.
//
// Beides läuft über dieselbe Schnittstelle; die Kündigungsseite ist das
// Formular mit festem Anliegen und eigener Bestätigungsseite (Inhalt,
// Datum und Uhrzeit des Eingangs). Angemeldete Kunden kündigen direkt unter
// „Tarif" — dort gilt die Kündigung sofort.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { Auswahl, Feld, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';
import RechtsRahmen from '../komponenten/RechtsRahmen';

const GRUENDE: Array<[string, string]> = [
  ['frage', 'Frage zu LagerPro'], ['support', 'Technische Hilfe'], ['rueckerstattung', 'Rückerstattung'],
  ['datenschutz', 'Datenschutz'], ['sonstiges', 'Sonstiges'],
];

function Formular({ kuendigung }: { kuendigung: boolean }) {
  const [grund, setGrund] = useState(kuendigung ? 'kuendigung' : 'frage');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [firma, setFirma] = useState('');
  const [kennung, setKennung] = useState('');
  const [rechnungsnummer, setRechnungsnummer] = useState('');
  const [nachricht, setNachricht] = useState('');
  const [website, setWebsite] = useState(''); // unsichtbar — nur Bots füllen es aus
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [eingang, setEingang] = useState<string | null>(null);

  async function senden(e: React.FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api<{ eingang: string }>('/oeffentlich/kontakt', {
        methode: 'POST',
        daten: { grund, name, email, firma, kennung, rechnungsnummer: grund === 'rueckerstattung' ? rechnungsnummer : '', nachricht, website },
      });
      setEingang(r.eingang);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Senden fehlgeschlagen. Bitte später erneut versuchen.'); }
    finally { setLaeuft(false); }
  }

  if (eingang) {
    const zeit = new Date(eingang).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' });
    return kuendigung ? (
      <div className="space-y-3">
        <Hinweis art="erfolg" titel="Ihre Kündigung ist eingegangen">
          Eingang: {zeit} Uhr. Gekündigt wird der Vertrag der Firma „{firma}" ordentlich zum nächstmöglichen
          Termin. Eine Eingangsbestätigung ist an {email} unterwegs; das genaue Vertragsende bestätigen wir
          gesondert, sobald wir die Kündigung Ihrem Konto zugeordnet haben.
        </Hinweis>
        <p className="text-sm">Bitte bewahren Sie diese Bestätigung auf.</p>
      </div>
    ) : (
      <Hinweis art="erfolg" titel="Danke für Ihre Nachricht">
        Eingang: {zeit} Uhr. Eine Bestätigung ist an {email} unterwegs; wir melden uns so bald wie möglich.
      </Hinweis>
    );
  }

  return (
    <form onSubmit={senden} className="mt-4 space-y-4">
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {!kuendigung && (
        <Auswahl label="Ihr Anliegen" value={grund} onChange={e => setGrund(e.target.value)}>
          {GRUENDE.map(([w, n]) => <option key={w} value={w}>{n}</option>)}
        </Auswahl>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Feld label="Ihr Name" value={name} required onChange={e => setName(e.target.value)} autoComplete="name" />
        <Feld label="E-Mail" type="email" value={email} required onChange={e => setEmail(e.target.value)} autoComplete="email" />
        <Feld label={kuendigung ? 'Firma (Vertragspartner)' : 'Firma (optional)'} value={firma} required={kuendigung}
              onChange={e => setFirma(e.target.value)} autoComplete="organization" />
        {(kuendigung || grund === 'support') && (
          <Feld label="Benutzername oder Kundennummer (optional)" value={kennung} onChange={e => setKennung(e.target.value)} />
        )}
        {grund === 'rueckerstattung' && (
          <Feld label="Rechnungsnummer" value={rechnungsnummer} required onChange={e => setRechnungsnummer(e.target.value)} />
        )}
      </div>
      {kuendigung && (
        <Hinweis art="info">Art der Kündigung: ordentlich, zum nächstmöglichen Termin.</Hinweis>
      )}
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {kuendigung ? 'Anmerkung (optional)' : grund === 'rueckerstattung' ? 'Begründung' : 'Nachricht'}
        </span>
        <textarea value={nachricht} onChange={e => setNachricht(e.target.value)} required={!kuendigung} rows={5} maxLength={5000}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
      </label>
      {/* Für Menschen unsichtbar — füllt es jemand aus, war es ein Bot. */}
      <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)}
             tabIndex={-1} autoComplete="off" aria-hidden className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <Knopf type="submit" variante={kuendigung ? 'gefahr' : 'primaer'} laedt={laeuft} className="w-full sm:w-auto">
        {kuendigung ? 'Jetzt kündigen' : 'Nachricht senden'}
      </Knopf>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Wir verarbeiten Ihre Angaben nur zur Bearbeitung Ihres Anliegens — siehe <Link to="/datenschutz">Datenschutzerklärung</Link>.
      </p>
    </form>
  );
}

export function Kontakt() {
  return (
    <RechtsRahmen titel="Kontakt">
      <p>
        Fragen, technische Hilfe oder eine Rückerstattung? Schreiben Sie uns — oder per E-Mail an
        <a href="mailto:info@quantory.de"> info@quantory.de</a>. Einen Vertrag kündigen Sie über
        <Link to="/kuendigen"> „Verträge hier kündigen"</Link>.
      </p>
      <Formular kuendigung={false} />
    </RechtsRahmen>
  );
}

export function VertraegeKuendigen() {
  return (
    <RechtsRahmen titel="Verträge hier kündigen">
      <p>
        Hier kündigen Sie Ihren LagerPro-Vertrag ohne Anmeldung. Sind Sie angemeldet, geht es schneller unter
        „Tarif" in der App — dort gilt die Kündigung sofort und lässt sich bis zum Vertragsende zurücknehmen.
      </p>
      <Formular kuendigung />
    </RechtsRahmen>
  );
}
